import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Alert, AlertDescription } from "./ui/alert";
import { Separator } from "./ui/separator";
import { RefreshCw, Download, AlertCircle, CheckCircle, BarChart3 } from "lucide-react";
import axios from "axios";

const API = require('../services/apiBase').default || require('../services/apiBase');

const AllocationPreview = ({ calculationId, allocationResults = [], onResultsUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (Array.isArray(allocationResults) && allocationResults.length > 0) {
      setResults(allocationResults);
    }
  }, [allocationResults]);

  useEffect(() => {
    if (calculationId) load();
  }, [calculationId]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/allocation-results/${calculationId}`);
      const list = res.data.allocation_results || [];
      setResults(list);
      onResultsUpdate && onResultsUpdate(list);
    } catch (e) {
      if (e.response?.status !== 404) setError(e.response?.data?.detail || 'Error loading allocation results');
    } finally {
      setLoading(false);
    }
  };

  const recalc = async () => {
    setCalculating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await axios.post(`${API}/calculate-allocations/${calculationId}`, { performed_by: 'user' });
      if (res.data.success) {
        setSuccess('Allocations calculated successfully');
        await load();
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Error calculating allocations');
    } finally {
      setCalculating(false);
    }
  };

  const exportCsv = () => {
    if (!results || results.length === 0) return alert('No results to export');
    const headers = ['Profile', 'Source Pool', 'Target Pool', 'Method', 'Percent', 'Amount', 'Business Unit'];
    const rows = [];
    results.forEach(r => {
      const rowsInR = r?.results?.allocation_results || [];
      rowsInR.forEach(a => rows.push([
        r.profile_name || r.profile_id || 'N/A',
        a.source_pool_name || a.source_pool_id || 'N/A',
        a.target_pool_id || 'N/A',
        a.allocation_method || 'N/A',
        a.allocation_percentage != null ? `${a.allocation_percentage}%` : 'N/A',
        a.allocated_amount != null ? `$${Number(a.allocated_amount).toLocaleString()}` : 'N/A',
        a.business_unit_name || 'N/A'
      ]));
    });
    const csv = [headers, ...rows].map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `Allocation_Results_${(calculationId||'').slice(0,8)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
  };

  const totalAmount = results.reduce((sum, r) => sum + (r?.results?.allocation_results || []).reduce((s, a) => s + (a.allocated_amount || 0), 0), 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-indigo-600"/> Allocation Preview</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className="h-4 w-4 mr-1"/>Refresh</Button>
            <Button variant="outline" onClick={exportCsv} disabled={!results || results.length===0}><Download className="h-4 w-4 mr-1"/>Export</Button>
            <Button onClick={recalc} disabled={calculating} className="bg-indigo-600 hover:bg-indigo-700">{calculating? 'Calculating...' : 'Recalculate'}</Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert className="border-red-200 bg-red-50 mb-4"><AlertCircle className="h-4 w-4"/><AlertDescription className="text-red-700">{error}</AlertDescription></Alert>
        )}
        {success && (
          <Alert className="border-green-200 bg-green-50 mb-4"><CheckCircle className="h-4 w-4"/><AlertDescription className="text-green-700">{success}</AlertDescription></Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div className="bg-blue-50 p-4 rounded"><div className="text-sm text-blue-900">Profiles</div><div className="text-2xl font-bold text-blue-600">{results.length}</div></div>
          <div className="bg-green-50 p-4 rounded"><div className="text-sm text-green-900">Allocations</div><div className="text-2xl font-bold text-green-600">{results.reduce((c, r)=> c + (r?.results?.allocation_results?.length||0), 0)}</div></div>
          <div className="bg-blue-50 p-4 rounded"><div className="text-sm text-blue-900">Total Amount</div><div className="text-2xl font-bold text-blue-600">${totalAmount.toLocaleString()}</div></div>
        </div>
        <Separator className="mb-4"/>
        {(results || []).map((r, idx) => (
          <div key={idx} className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold">{r.profile_name || r.profile_id || `Profile ${idx+1}`}</div>
              <Badge variant="outline" className="text-xs">{r.results?.allocation_method || 'allocation'}</Badge>
            </div>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Source Pool</TableHead>
                    <TableHead>Target Pool</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead className="text-right">Percent</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>Business Unit</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(r?.results?.allocation_results || []).map((a, i) => (
                    <TableRow key={i}>
                      <TableCell>{a.source_pool_name || a.source_pool_id}</TableCell>
                      <TableCell>{a.target_pool_id}</TableCell>
                      <TableCell>{a.allocation_method}</TableCell>
                      <TableCell className="text-right">{a.allocation_percentage != null ? `${a.allocation_percentage}%` : '-'}</TableCell>
                      <TableCell className="text-right">{a.allocated_amount != null ? `$${Number(a.allocated_amount).toLocaleString()}` : '-'}</TableCell>
                      <TableCell>{a.business_unit_name || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        ))}
        {(!results || results.length===0) && (
          <div className="text-center py-8 text-slate-500">No allocation results yet. Click Recalculate to generate.</div>
        )}
      </CardContent>
    </Card>
  );
};

export default AllocationPreview;